import React, { useState, useRef } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { X, RotateCw, Check } from "lucide-react";
import { useI18nStore } from "@/stores/i18nStore";
import { i18nToast } from "@/utils/i18nToast";

interface ImageCropModalProps {
  imageFile: File;
  onCropComplete: (croppedFile: File, cropData: PixelCrop) => void;
  onCancel: () => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageFile,
  onCropComplete,
  onCancel,
}) => {
  const { t } = useI18nStore();
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 80,
    height: 80,
    x: 10,
    y: 10,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgSrc, setImgSrc] = useState<string>("");

  React.useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result as string);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  // ReactCrop reports a completed crop only after the user drags, so without
  // this the Apply button stayed disabled for anyone who accepted the default.
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    if (!width || !height) return;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, 1, width, height),
      width,
      height,
    );
    setCrop(initial);
    setCompletedCrop(convertToPixelCrop(initial, width, height));
  };

  const failed = (reason: unknown) => {
    console.error("Image crop failed:", reason);
    i18nToast.error(
      t.photoCaptureFailed || "Could not use the photo. Please try again.",
    );
  };

  const handleComplete = () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      failed("2D canvas context unavailable");
      return;
    }

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Output at the source resolution, not the on-screen size: on a phone the
    // displayed crop is often under the backend's 200x200 minimum.
    canvas.width = Math.round(completedCrop.width * scaleX);
    canvas.height = Math.round(completedCrop.height * scaleY);

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          failed("canvas.toBlob returned null");
          return;
        }
        const croppedFile = new File([blob], imageFile.name, {
          type: "image/jpeg",
        });
        onCropComplete(croppedFile, completedCrop);
      },
      "image/jpeg",
      0.95,
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {t.cropImage || "Crop Image"}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Crop Area */}
        <div className="p-6 overflow-auto max-h-[60vh]">
          {imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              circularCrop
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Crop preview"
                onLoad={handleImageLoad}
                style={{
                  transform: `rotate(${rotation}deg)`,
                  maxWidth: "100%",
                }}
              />
            </ReactCrop>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleRotate}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RotateCw className="w-5 h-5" />
            {t.rotate || "Rotate"}
          </button>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.cancel || "Cancel"}
            </button>
            <button
              onClick={handleComplete}
              disabled={!completedCrop}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Check className="w-5 h-5" />
              {t.apply || "Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
